import React, { useMemo } from "react";
import ForceGraph2D from "react-force-graph-2d";
import { Matrix } from "../../floyd/floydAlgorithm";
// eslint-disable-next-line
import { KruskalStep, Edge } from "./kruskalsAlgorithm";

type KruskalsGraphViewProps = {
  matrix: Matrix;
  labels: string[];
  step: KruskalStep | null;
};

interface GraphNode {
  id: string;
  label: string;
  isInMST: boolean;
}

interface GraphLink {
  source: string;
  target: string;
  weight: number;
  isInMST: boolean;
  isConsidered: boolean;
  isRejected: boolean;
}

export default function KruskalsGraphView({
  matrix,
  labels,
  step,
}: KruskalsGraphViewProps) {
  const graphData = useMemo(() => {
    const nodes: GraphNode[] = [];
    const links: GraphLink[] = [];

    if (!step) {
      for (let i = 0; i < matrix.length; i++) {
        nodes.push({
          id: labels[i],
          label: labels[i],
          isInMST: false,
        });
      }
    } else {
      const mstVertices = new Set<number>();
      step.mstEdges.forEach((e) => {
        mstVertices.add(e.from);
        mstVertices.add(e.to);
      });

      for (let i = 0; i < matrix.length; i++) {
        nodes.push({
          id: labels[i],
          label: labels[i],
          isInMST: mstVertices.has(i),
        });
      }
    }

    // Create links
    for (let i = 0; i < matrix.length; i++) {
      for (let j = i + 1; j < matrix.length; j++) {
        const weight = matrix[i][j];
        if (Number.isFinite(weight) && weight > 0) {
          const isInMST =
            step?.mstEdges.some(
              (e) =>
                (e.from === i && e.to === j) || (e.from === j && e.to === i)
            ) ?? false;
          const isConsidered =
            step?.consideredEdge &&
            ((step.consideredEdge.from === i && step.consideredEdge.to === j) ||
              (step.consideredEdge.from === j && step.consideredEdge.to === i));
          const isRejected =
            step?.rejectedEdge &&
            ((step.rejectedEdge.from === i && step.rejectedEdge.to === j) ||
              (step.rejectedEdge.from === j && step.rejectedEdge.to === i));

          links.push({
            source: labels[i],
            target: labels[j],
            weight: weight,
            isInMST,
            isConsidered: isConsidered ?? false,
            isRejected: isRejected ?? false,
          });
        }
      }
    }

    return { nodes, links };
  }, [matrix, labels, step]);

  return (
    <div className="graph-container">
      <ForceGraph2D
        graphData={graphData}
        nodeLabel={(node: any) => node.label}
        linkLabel={(link: GraphLink) => `${link.weight}`}
        nodeColor={(node: GraphNode) => {
          if (node.isInMST) return "#10b981";
          return "#60a5fa";
        }}
        linkColor={(link: GraphLink) => {
          if (link.isInMST) return "#10b981";
          if (link.isRejected) return "#ef4444";
          if (link.isConsidered) return "#f59e0b";
          return "#9bb0c3";
        }}
        linkWidth={(link: GraphLink) => {
          if (link.isInMST) return 4;
          if (link.isConsidered || link.isRejected) return 3;
          return 2;
        }}
        backgroundColor="transparent"
        width={800}
        height={500}
        nodeCanvasObject={(
          node: any,
          ctx: CanvasRenderingContext2D,
          globalScale: number
        ) => {
          const label = node.label;
          const fontSize = 16 / globalScale;
          const nodeRadius = 30 / globalScale;

          let color = "#60a5fa";
          if (node.isInMST) color = "#10b981";

          ctx.beginPath();
          ctx.arc(node.x, node.y, nodeRadius, 0, 2 * Math.PI, false);
          ctx.fillStyle = color;
          ctx.fill();
          ctx.strokeStyle = "#e7edf5";
          ctx.lineWidth = 3 / globalScale;
          ctx.stroke();

          ctx.font = `bold ${fontSize}px Sans-Serif`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillStyle = "#e7edf5";
          ctx.fillText(label, node.x, node.y);
        }}
        linkDirectionalArrowLength={0}
        cooldownTicks={200}
        enableZoomInteraction={true}
        enableNodeDrag={true}
      />
      <div style={{ marginTop: "1rem", fontSize: "0.9rem", color: "#9bb0c3" }}>
        <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
          <span>
            <span
              style={{
                display: "inline-block",
                width: "12px",
                height: "12px",
                backgroundColor: "#10b981",
                borderRadius: "50%",
                marginRight: "4px",
              }}
            />
            In MST
          </span>
          <span>
            <span
              style={{
                display: "inline-block",
                width: "12px",
                height: "12px",
                backgroundColor: "#f59e0b",
                borderRadius: "50%",
                marginRight: "4px",
              }}
            />
            Considering
          </span>
          <span>
            <span
              style={{
                display: "inline-block",
                width: "12px",
                height: "12px",
                backgroundColor: "#ef4444",
                borderRadius: "50%",
                marginRight: "4px",
              }}
            />
            Rejected (Cycle)
          </span>
          <span>
            <span
              style={{
                display: "inline-block",
                width: "12px",
                height: "12px",
                backgroundColor: "#9bb0c3",
                borderRadius: "50%",
                marginRight: "4px",
              }}
            />
            Not Yet Considered
          </span>
        </div>
      </div>
    </div>
  );
}

